import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { CompanySelector } from "./CompanySelector";
import { LoadingAlerts } from "./LoadingAlerts";
import { LaptopBanner } from "./LaptopBanner";
import {
  SquaresFour, Scan, UsersThree, ShoppingCart, VideoCamera,
  TreeStructure, ShieldCheck, SignOut, Clock, Broadcast,
  ListChecks, Plugs, Ruler, HardDrives, BellRinging, ArrowsClockwise,
  GlobeHemisphereWest, CloudSun, MapTrifold, Buildings, MapPinArea,
  Television, WarningDiamond, Brain, IdentificationCard, List, X, Monitor, FilmSlate, Fire, Path, Car, Crosshair, Truck, ChartBar,
} from "@phosphor-icons/react";

const NAV = [
  { to: "/", label: "Command Center", icon: SquaresFour, end: true, section: "Operations", core: true },
  { to: "/watch", label: "Live Watch", icon: Broadcast, section: "Operations", core: true },
  { to: "/eye", label: "Eye · Track Person", icon: Crosshair, section: "Operations", core: true, mp: true },
  { to: "/patrol", label: "Patrol Mode", icon: ArrowsClockwise, section: "Operations" },
  { to: "/analyzer", label: "AI Vision Analyzer", icon: Scan, section: "Operations" },
  { to: "/incidents", label: "Incident Log", icon: ListChecks, section: "Operations", core: true },
  { to: "/events", label: "Event History", icon: FilmSlate, section: "Operations", core: true },
  { to: "/wall", label: "Surveillance Wall", icon: Television, section: "Surveillance TV" },
  { to: "/spotlight", label: "Alert Spotlight", icon: WarningDiamond, section: "Surveillance TV" },
  { to: "/brain", label: "Command Brain", icon: Brain, section: "Surveillance TV" },
  { to: "/people", label: "People & ID", icon: IdentificationCard, section: "Surveillance TV", core: true },
  { to: "/displays", label: "TV Displays", icon: Monitor, section: "Surveillance TV" },
  { to: "/office", label: "Office Board", icon: Monitor, section: "Surveillance TV", core: true },
  { to: "/employees", label: "Employee Effectiveness", icon: UsersThree, section: "Analytics", core: true },
  { to: "/movement", label: "Movement Trail", icon: Path, section: "Analytics", core: true },
  { to: "/areas", label: "Work Areas", icon: MapPinArea, section: "Analytics" },
  { to: "/customers", label: "Customer Interaction", icon: ShoppingCart, section: "Analytics", core: true },
  { to: "/store-heatmap", label: "Store Heat Map", icon: Fire, section: "Analytics", core: true },
  { to: "/hotzones", label: "Hot Zones", icon: Fire, section: "Analytics", core: true },
  { to: "/intel", label: "Area Intel", icon: CloudSun, section: "Situational Awareness", master: true },
  { to: "/global", label: "Global Watch", icon: GlobeHemisphereWest, section: "Situational Awareness", master: true },
  { to: "/map", label: "Map View", icon: MapTrifold, section: "Situational Awareness", master: true },
  { to: "/board", label: "Store Board", icon: Buildings, section: "Situational Awareness", master: true },
  { to: "/connect", label: "Camera Connect", icon: Plugs, section: "Setup" },
  { to: "/agent", label: "Edge Agent", icon: HardDrives, section: "Setup", master: true },
  { to: "/cameras", label: "Camera Compatibility", icon: VideoCamera, section: "Setup" },
  { to: "/planner", label: "Coverage Planner", icon: Ruler, section: "Setup", master: true },
  { to: "/settings", label: "Alert Delivery", icon: BellRinging, section: "Setup", master: true },
  { to: "/architecture", label: "System Architecture", icon: TreeStructure, section: "Setup", master: true },
  { to: "/avstone/dashboard", label: "A V Stone Dashboard", icon: Buildings, section: "A V Stone", avstone: true },
  { to: "/avstone/live", label: "A V Stone Live Feed", icon: Broadcast, section: "A V Stone", avstone: true },
  { to: "/avstone/plates", label: "A V Stone Number Plates", icon: Car, section: "A V Stone", avstone: true },
  { to: "/avstone/loading", label: "A V Stone Truck Loading", icon: Truck, section: "A V Stone", avstone: true },
  { to: "/avstone/study", label: "A V Stone Camera Study", icon: ChartBar, section: "A V Stone", avstone: true },
  { to: "/avstone", label: "A V Stone Setup", icon: Buildings, section: "A V Stone", avstone: true },
];

// Primary destinations for the mobile bottom tab bar (all roles).
const BOTTOM_TABS = [
  { to: "/", label: "Command", icon: SquaresFour, end: true, testid: "tab-command-center" },
  { to: "/watch", label: "Live", icon: Broadcast, testid: "tab-live-watch" },
  { to: "/wall", label: "TV Wall", icon: Television, testid: "tab-surveillance-wall" },
  { to: "/people", label: "People", icon: IdentificationCard, testid: "tab-people-id" },
  { to: "/incidents", label: "Alerts", icon: ListChecks, testid: "tab-alerts" },
];

const TITLES = {
  "/": "Command Center",
  "/watch": "Live Watch",
  "/wall": "Surveillance Wall",
  "/spotlight": "Alert Spotlight",
  "/brain": "Command Brain",
  "/people": "People & Identification",
  "/areas": "Work Areas & Departments",
  "/displays": "TV Displays",
  "/events": "Event History",
  "/patrol": "Patrol Mode",
  "/analyzer": "AI Vision Analyzer",
  "/incidents": "Incident Log",
  "/employees": "Employee Effectiveness",
  "/movement": "Movement Trail",
  "/customers": "Customer Interaction",
  "/hotzones": "Hot Zones & Heatmap",
  "/eye": "Eye · Track Person",
  "/store-heatmap": "Store Heat Map",
  "/avstone/dashboard": "A V Stone Dashboard",
  "/avstone/live": "A V Stone Live Feed",
  "/avstone/plates": "A V Stone Number Plates",
  "/avstone/loading": "A V Stone Truck Loading",
  "/avstone/study": "A V Stone Camera Study",
  "/avstone": "A V Stone Setup",
  "/intel": "Area Intel",
  "/global": "Global Watch",
  "/map": "Situational Map",
  "/board": "Multi-Store Board",
  "/connect": "Camera Connect",
  "/agent": "Edge Agent",
  "/cameras": "Camera Compatibility Guide",
  "/planner": "Store Coverage Planner",
  "/settings": "Alert Delivery",
  "/architecture": "System Architecture",
};

function NavList({ visibleNav, onNavigate }) {
  return (
    <nav className="flex-1 py-4 px-3 space-y-0.5 overflow-y-auto">
      {visibleNav.map(({ to, label, icon: Icon, end, section }, i) => (
        <div key={to}>
          {(i === 0 || visibleNav[i - 1].section !== section) && (
            <p className="px-3 pt-3 pb-1 text-[10px] uppercase tracking-[0.2em] text-[#52525B] font-mono-data">{section}</p>
          )}
          <NavLink
            to={to}
            end={end}
            onClick={onNavigate}
            data-testid={`nav-${to === "/" ? "dashboard" : to.slice(1)}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 text-sm transition-colors border-l-2 min-h-[44px] ${
                isActive
                  ? "bg-[#007AFF]/10 border-[#007AFF] text-white"
                  : "border-transparent text-[#A1A1AA] hover:text-white hover:bg-white/5"
              }`
            }
          >
            <Icon size={18} weight="bold" />
            <span className="tracking-tight">{label}</span>
          </NavLink>
        </div>
      ))}
    </nav>
  );
}

function UserFooter({ user, isMaster, logout }) {
  return (
    <div className="p-3 border-t border-white/10">
      <div className="flex items-center gap-3 px-2 py-2">
        <div className="w-9 h-9 bg-[#007AFF]/20 border border-[#007AFF]/40 flex items-center justify-center font-mono-data text-xs text-[#3395FF]">
          {(user?.name || "OP").slice(0, 2).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm truncate">{user?.name || "Operator"}</p>
          <p className="text-xs font-mono-data truncate">
            <span className={isMaster ? "text-[#32D74B]" : "text-[#FF9F0A]"}>
              {isMaster ? (user?.lock_store ? "ADMIN · MarketPlace only" : "MASTER · all sites") : `STAFF · ${(user?.store_names || []).join(", ") || "scoped"}`}
            </span>
          </p>
        </div>
        <button data-testid="logout-button" onClick={logout} className="w-10 h-10 flex items-center justify-center text-[#71717A] hover:text-[#FF3B30] transition-colors">
          <SignOut size={20} />
        </button>
      </div>
      <p className="text-center text-[10px] font-mono-data tracking-[0.2em] text-[#3f3f46] uppercase pt-2">Powered by A V Tech</p>
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const isMaster = user?.role === "master" || user?.role === "admin";
  const isAvStone = (user?.stores || []).includes("store-av-stone");
  const locked = !!user?.lock_store;   // admin pinned to one company (Salman = MarketPlace only)
  const avOnly = isAvStone && !isMaster;
  // Master (PIN 83528) sees everything. Staff/marketplace (PIN 7861) sees only the
  // day-to-day "core" pages — trims the sidebar ~50% and hides setup/situational-awareness clutter.
  const visibleNav = NAV.filter((n) => {
    if (n.avstone) return (isMaster && !locked) || isAvStone;   // A V Stone: master or A V Stone staff, never locked admins
    if (n.mp && avOnly) return false;   // MarketPlace-only pages never shown to A V Stone logins
    if (n.master && !isMaster) return false;
    if (!isMaster && !n.core) return false;
    return true;
  });
  const [now, setNow] = useState(new Date().toLocaleTimeString());
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date().toLocaleTimeString()), 1000);
    return () => clearInterval(t);
  }, []);

  // Close the mobile drawer whenever the route changes.
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  // A V Stone TV wall: bottom tabs float over the grid and auto-hide; tap/move to reveal for 4s.
  const tvWall = location.pathname === "/avstone/live";
  const [navShow, setNavShow] = useState(false);
  useEffect(() => {
    if (!tvWall) return undefined;
    let t;
    const show = () => { setNavShow(true); clearTimeout(t); t = setTimeout(() => setNavShow(false), 4000); };
    window.addEventListener("pointerdown", show);
    window.addEventListener("mousemove", show);
    return () => { clearTimeout(t); window.removeEventListener("pointerdown", show); window.removeEventListener("mousemove", show); };
  }, [tvWall]);

  const title = TITLES[location.pathname] || "Command Center";

  return (
    <div className="min-h-screen bg-[#0A0A0C]">
      {/* Desktop sidebar (lg+) */}
      <aside className="hidden lg:flex w-64 shrink-0 border-r border-white/10 bg-[#0A0A0C] flex-col fixed h-screen z-20">
        <div className="h-16 flex items-center gap-2 px-5 border-b border-white/10">
          <ShieldCheck size={26} weight="fill" className="text-[#007AFF]" />
          <span className="font-display font-black text-lg tracking-tighter uppercase">SentryVision</span>
        </div>
        <NavList visibleNav={visibleNav} />
        <UserFooter user={user} isMaster={isMaster} logout={logout} />
      </aside>

      {/* Mobile top app bar (below lg) */}
      <header className="lg:hidden fixed top-0 inset-x-0 h-14 z-40 bg-[#0A0A0C]/90 backdrop-blur-md border-b border-white/10 px-2 flex items-center gap-2">
        <button
          data-testid="mobile-menu-button"
          onClick={() => setDrawerOpen(true)}
          aria-label="Open menu"
          className="w-11 h-11 flex items-center justify-center text-white active:bg-white/10 rounded"
        >
          <List size={24} weight="bold" />
        </button>
        <div className="min-w-0 flex-1">
          <p className="font-mono-data text-[9px] text-[#71717A] uppercase tracking-[0.2em] leading-none">SentryVision AI</p>
          <h1 className="font-display font-black text-base tracking-tighter uppercase leading-tight truncate">{title}</h1>
        </div>
        <span className="flex items-center gap-1.5 font-mono-data text-[10px] text-[#32D74B] pr-1">
          <span className="w-2 h-2 rounded-full bg-[#32D74B] live-dot" /> LIVE
        </span>
      </header>

      {/* Mobile slide-in drawer */}
      <div className={`lg:hidden fixed inset-0 z-50 ${drawerOpen ? "" : "pointer-events-none"}`}>
        <div
          data-testid="mobile-drawer-overlay"
          onClick={() => setDrawerOpen(false)}
          className={`absolute inset-0 bg-black/70 backdrop-blur-sm transition-opacity duration-200 ${drawerOpen ? "opacity-100" : "opacity-0"}`}
        />
        <aside
          data-testid="mobile-drawer"
          className={`absolute left-0 top-0 h-full w-[84%] max-w-[320px] bg-[#0A0A0C] border-r border-white/10 flex flex-col transition-transform duration-200 ${drawerOpen ? "translate-x-0" : "-translate-x-full"}`}
        >
          <div className="h-14 flex items-center justify-between gap-2 px-4 border-b border-white/10">
            <div className="flex items-center gap-2">
              <ShieldCheck size={24} weight="fill" className="text-[#007AFF]" />
              <span className="font-display font-black text-lg tracking-tighter uppercase">SentryVision</span>
            </div>
            <button
              data-testid="mobile-drawer-close"
              onClick={() => setDrawerOpen(false)}
              aria-label="Close menu"
              className="w-10 h-10 flex items-center justify-center text-[#A1A1AA] hover:text-white"
            >
              <X size={22} weight="bold" />
            </button>
          </div>
          {isMaster && !locked && (
            <div className="px-4 py-3 border-b border-white/10" data-testid="company-selector-mobile">
              <CompanySelector />
            </div>
          )}
          <NavList visibleNav={visibleNav} onNavigate={() => setDrawerOpen(false)} />
          <UserFooter user={user} isMaster={isMaster} logout={logout} />
        </aside>
      </div>

      {/* Mobile bottom tab bar */}
      <nav
        data-testid="mobile-bottom-nav"
        className={`lg:hidden fixed bottom-0 inset-x-0 bg-[#0A0A0C]/95 backdrop-blur-lg border-t border-white/10 grid grid-cols-5 transition-transform duration-300 ${tvWall ? `z-[60] ${navShow ? "" : "translate-y-full"}` : "z-40"}`}
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        {BOTTOM_TABS.map(({ to, label, icon: Icon, end, testid }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            data-testid={testid}
            className={({ isActive }) =>
              `flex flex-col items-center justify-center gap-1 h-16 transition-colors ${
                isActive ? "text-[#007AFF]" : "text-[#71717A] active:text-white"
              }`
            }
          >
            {({ isActive }) => (
              <>
                <Icon size={22} weight={isActive ? "fill" : "bold"} />
                <span className="font-mono-data text-[9px] uppercase tracking-wider">{label}</span>
              </>
            )}
          </NavLink>
        ))}
      </nav>

      {/* Main content */}
      <div className="lg:pl-64 flex flex-col min-h-screen min-w-0">
        {/* Desktop header (lg+) */}
        <header className="hidden lg:flex h-16 border-b border-white/10 bg-[#0A0A0C]/80 backdrop-blur-xl sticky top-0 z-10 items-center justify-between px-6">
          <div>
            <p className="font-mono-data text-[10px] text-[#71717A] uppercase tracking-widest">SentryVision AI · Live</p>
            <h1 className="font-display font-black text-lg tracking-tighter uppercase leading-none">{title}</h1>
          </div>
          <div className="flex items-center gap-4">
            {isMaster && !locked && <CompanySelector />}
            <span className="font-display font-bold tracking-[0.25em] uppercase text-xs text-[#0A84FF]" data-testid="header-branding">Powered by A V Tech</span>
            <div className="flex items-center gap-2 font-mono-data text-xs text-[#A1A1AA]">
              <Clock size={14} weight="bold" className="text-[#71717A]" />
              {now}
            </div>
            <div className="flex items-center gap-2 font-mono-data text-xs text-[#32D74B]">
              <span className="w-2 h-2 rounded-full bg-[#32D74B] live-dot" />
              SYSTEM ONLINE
            </div>
          </div>
        </header>
        <main className="flex-1 px-4 py-4 pt-[4.75rem] pb-24 lg:p-6 lg:pt-6 lg:pb-6 min-w-0">
          <Outlet />
          {isAvStone && <LoadingAlerts />}
          {(isAvStone || (isMaster && !user?.lock_store)) && <LaptopBanner />}
        </main>
      </div>
    </div>
  );
}
